import type { AppState, Operation } from "@/lib/types";

/**
 * Apply a list of operations to an AppState
 * This is used to merge server changes into local state
 *
 * @param state - Current app state
 * @param operations - Server operations to apply (must be a valid array)
 * @param pendingOperations - Local operations not yet synced (to avoid conflicts)
 */
export function applyOperations(
  state: AppState,
  operations: Operation[],
  pendingOperations: Operation[] = []
): AppState {
  // Fast-path - ensure operations is a valid array
  if (!operations || !Array.isArray(operations) || operations.length === 0) {
    console.log("⏭️ No operations to apply, returning current state");
    return state;
  }

  console.log(
    `🔄 Applying ${operations.length} server operation(s), ${pendingOperations.length} pending operation(s)`
  );

  // Build a set of entity IDs that have pending local changes
  const pendingIngredientIds = new Set<string>();
  const pendingListIds = new Set<string>();

  for (const op of pendingOperations) {
    if (op.type === "UPDATE_INGREDIENT" || op.type === "DELETE_INGREDIENT") {
      pendingIngredientIds.add(op.id);
    } else if (op.type === "UPDATE_LIST" || op.type === "DELETE_LIST") {
      pendingListIds.add(op.id);
    }
  }

  // Build quick lookup maps from current state
  const lists = state.lists || [];
  const listById = new Map<string, (typeof lists)[number]>();
  const ingredientToList = new Map<string, string>();
  for (const list of lists) {
    listById.set(list.id, list);
    for (const ing of list.ingredients) {
      ingredientToList.set(ing.id, list.id);
    }
  }

  // We'll lazily copy ingredients arrays only for lists that change
  const ingredientsCopy = new Map<string, (typeof lists)[number]["ingredients"]>();
  const updatedListMeta = new Map<string, Partial<(typeof lists)[number]>>();
  const deletedListIds = new Set<string>();
  const addedLists: (typeof lists)[number][] = [];
  const addedListIds = new Set<string>(); // Track IDs of newly added lists

  const shouldSkipOp = (op: Operation) => {
    return (
      (op.type === "UPDATE_INGREDIENT" && pendingIngredientIds.has(op.id)) ||
      (op.type === "DELETE_INGREDIENT" && pendingIngredientIds.has(op.id)) ||
      (op.type === "UPDATE_LIST" && pendingListIds.has(op.id)) ||
      (op.type === "DELETE_LIST" && pendingListIds.has(op.id))
    );
  };

  for (const op of operations) {
    if (shouldSkipOp(op)) {
      console.log(
        "⏭️  Skipping server operation (pending local change):",
        op.type,
        (op as any).id || (op as any).list?.id
      );
      continue;
    }

    switch (op.type) {
      case "ADD_INGREDIENT": {
        const { listId, ingredient } = op as Extract<Operation, { type: "ADD_INGREDIENT" }>;
        let targetList = listById.get(listId);

        // If list doesn't exist in current state, check if it's being added in this batch
        if (!targetList && addedListIds.has(listId)) {
          targetList = addedLists.find((l) => l.id === listId);
        }

        if (!targetList) {
          // If the target list doesn't exist locally and isn't being added, skip
          console.log(
            `⏭️  Skipping ADD_INGREDIENT for ${ingredient.id} (list ${listId} not found)`
          );
          break;
        }

        if (!ingredientsCopy.has(listId)) {
          ingredientsCopy.set(listId, [...targetList.ingredients]);
        }
        ingredientsCopy.get(listId)!.push(ingredient);
        break;
      }

      case "UPDATE_INGREDIENT": {
        const { id, changes } = op as Extract<Operation, { type: "UPDATE_INGREDIENT" }>;
        const listId = ingredientToList.get(id);
        if (!listId) break;
        const targetList = listById.get(listId);
        if (!targetList) break;

        if (!ingredientsCopy.has(listId)) {
          ingredientsCopy.set(listId, [...targetList.ingredients]);
        }

        const arr = ingredientsCopy.get(listId)!;
        const idx = arr.findIndex((i) => i.id === id);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], ...changes };
        }
        break;
      }

      case "DELETE_INGREDIENT": {
        const { id } = op as Extract<Operation, { type: "DELETE_INGREDIENT" }>;
        const listId = ingredientToList.get(id);
        if (!listId) break;
        const targetList = listById.get(listId);
        if (!targetList) break;

        if (!ingredientsCopy.has(listId)) {
          ingredientsCopy.set(listId, [...targetList.ingredients]);
        }

        ingredientsCopy.set(
          listId,
          ingredientsCopy.get(listId)!.filter((ing) => ing.id !== id)
        );
        break;
      }

      case "ADD_LIST": {
        const { list } = op as Extract<Operation, { type: "ADD_LIST" }>;
        // If list already exists, treat as an update to metadata + potential ingredients
        if (listById.has(list.id)) {
          updatedListMeta.set(list.id, { name: list.name, color: list.color });
          // Also handle ingredients if provided
          if (list.ingredients && list.ingredients.length > 0) {
            ingredientsCopy.set(list.id, [
              ...(listById.get(list.id)!.ingredients || []),
              ...list.ingredients,
            ]);
          }
        } else {
          // New list - add to addedLists for appending
          addedLists.push(list as any);
          addedListIds.add(list.id);
          // Pre-seed ingredient map for this new list
          if ((list as any).ingredients && (list as any).ingredients.length > 0) {
            ingredientsCopy.set(list.id, (list as any).ingredients.slice());
            for (const ing of (list as any).ingredients) {
              ingredientToList.set(ing.id, list.id);
            }
          }
        }
        break;
      }

      case "UPDATE_LIST": {
        const { id, changes } = op as Extract<Operation, { type: "UPDATE_LIST" }>;
        if (!listById.has(id)) {
          // List doesn't exist locally - create it with the provided metadata
          // This handles the case where:
          // 1. Server has a list created before lastSyncedAt
          // 2. Client doesn't have the list (localStorage was cleared or new device)
          // 3. Server sends UPDATE_LIST instead of ADD_LIST
          console.log(
            `📝 Creating missing list ${id} from UPDATE_LIST operation (list exists on server but not locally)`
          );
          const newList = {
            id,
            name: (changes as any).name || "Unnamed List",
            color: (changes as any).color || "#E74C3C",
            ingredients: [],
            ...(changes as any), // Include any other metadata (isShared, ownerId, etc.)
          } as any;
          addedLists.push(newList);
          addedListIds.add(id);
          // Initialize ingredient copy for this new list
          ingredientsCopy.set(id, []);
          break;
        }
        const existing = updatedListMeta.get(id) || {};
        updatedListMeta.set(id, { ...existing, ...changes });
        break;
      }

      case "DELETE_LIST": {
        const { id } = op as Extract<Operation, { type: "DELETE_LIST" }>;
        if (listById.has(id)) {
          deletedListIds.add(id);
        }
        break;
      }

      default: {
        // For any unknown type, fallback to the single-op applier
        // This is rare; applyOperation is stable but slower.
        // Build a small state snapshot to run it against.
        // We intentionally keep this path simple.
        state = applyOperation(state, op);
        break;
      }
    }
  }

  // Reconstruct lists array, preserving original order and applying changes lazily
  const newLists: typeof lists = [];
  for (const list of lists) {
    if (deletedListIds.has(list.id)) continue;

    let changed = false;
    const metaUpdates = updatedListMeta.get(list.id);
    const newIngredients = ingredientsCopy.has(list.id)
      ? ingredientsCopy.get(list.id)!
      : list.ingredients;

    if (metaUpdates && Object.keys(metaUpdates).length > 0) changed = true;
    if (ingredientsCopy.has(list.id)) changed = true;

    if (changed) {
      newLists.push({
        ...list,
        ...(metaUpdates || {}),
        ingredients: newIngredients,
      });
    } else {
      newLists.push(list);
    }
  }

  // Append newly added lists (in order of arrival)
  for (const added of addedLists) {
    if (!deletedListIds.has(added.id)) {
      // Use ingredientsCopy if available (includes ingredients added via ADD_INGREDIENT ops)
      const finalIngredients = ingredientsCopy.has(added.id)
        ? ingredientsCopy.get(added.id)!
        : added.ingredients;
      newLists.push({ ...added, ingredients: finalIngredients });
    }
  }

  // Adjust activeListId if it was deleted
  let newActive = state.activeListId;
  if (newActive && deletedListIds.has(newActive)) {
    newActive = newLists.length > 0 ? newLists[0].id : null;
  }

  return {
    ...state,
    lists: newLists,
    activeListId: newActive,
  };
}

/**
 * Apply a single operation to an AppState
 */
function applyOperation(state: AppState, operation: Operation): AppState {
  switch (operation.type) {
    case "ADD_INGREDIENT": {
      const { listId, ingredient } = operation;
      return {
        ...state,
        lists: state.lists.map((list) =>
          list.id === listId
            ? {
                ...list,
                ingredients: [...list.ingredients, ingredient],
              }
            : list
        ),
      };
    }

    case "UPDATE_INGREDIENT": {
      const { id, changes } = operation;
      return {
        ...state,
        lists: state.lists.map((list) => ({
          ...list,
          ingredients: list.ingredients.map((ing) =>
            ing.id === id ? { ...ing, ...changes } : ing
          ),
        })),
      };
    }

    case "DELETE_INGREDIENT": {
      const { id } = operation;
      return {
        ...state,
        lists: state.lists.map((list) => ({
          ...list,
          ingredients: list.ingredients.filter((ing) => ing.id !== id),
        })),
      };
    }

    case "ADD_LIST": {
      const { list } = operation;
      return {
        ...state,
        lists: [...state.lists, list],
      };
    }

    case "UPDATE_LIST": {
      const { id, changes } = operation;
      return {
        ...state,
        lists: state.lists.map((list) => (list.id === id ? { ...list, ...changes } : list)),
      };
    }

    case "DELETE_LIST": {
      const { id } = operation;
      const newLists = state.lists.filter((list) => list.id !== id);
      return {
        ...state,
        lists: newLists,
        // If we deleted the active list, switch to first available or null
        activeListId:
          state.activeListId === id
            ? newLists.length > 0
              ? newLists[0].id
              : null
            : state.activeListId,
      };
    }

    default:
      return state;
  }
}

/**
 * Create a timestamp for an operation
 */
export function createTimestamp(): string {
  return new Date().toISOString();
}
